<script setup lang="ts">
import { computed } from 'vue'
import { scansApi } from '@/api/scansApi'
import { formatBytes } from '@/utilities/download'
import type { ScanDto } from '@/models/library'

const props = defineProps<{ scan: ScanDto; selected: boolean }>()
const emit = defineEmits<{ (e: 'toggle'): void; (e: 'open'): void }>()

const isPdf = computed(() => props.scan.kind === 'Pdf')
const thumbUrl = computed(() => scansApi.thumbnailUrl(props.scan.id, props.scan.updatedUtc))
</script>

<template>
  <div class="scan-card" :class="{ selected }">
    <button class="thumb" type="button" @click="emit('open')" :aria-label="`Open ${scan.name}`">
      <img v-if="scan.hasThumbnail" :src="thumbUrl" :alt="scan.name" loading="lazy" />
      <span v-else class="thumb-icon">{{ isPdf ? '📄' : '🖼️' }}</span>
      <span v-if="isPdf" class="kind-badge">PDF</span>
    </button>

    <label class="select" @click.stop>
      <input type="checkbox" :checked="selected" @change="emit('toggle')" />
    </label>

    <div class="meta">
      <span class="name" :title="scan.name">{{ scan.name }}</span>
      <span class="size">{{ formatBytes(scan.sizeBytes) }}</span>
    </div>
  </div>
</template>

<style scoped>
.scan-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.scan-card.selected {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--primary) inset;
}

.thumb {
  position: relative;
  border: 0;
  padding: 0;
  background: var(--surface-2);
  aspect-ratio: 3 / 4;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-icon {
  font-size: 2.5rem;
}

.kind-badge {
  position: absolute;
  bottom: 0.4rem;
  right: 0.4rem;
  background: var(--danger);
  color: #3b0a0a;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
}

.select {
  position: absolute;
  top: 0.4rem;
  left: 0.4rem;
  background: rgba(15, 23, 42, 0.7);
  border-radius: 6px;
  padding: 0.15rem 0.3rem;
  display: inline-flex;
}

.select input {
  width: 1.1rem;
  height: 1.1rem;
  accent-color: var(--primary);
}

.meta {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.6rem;
  gap: 0.1rem;
}

.name {
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.size {
  font-size: 0.75rem;
  color: var(--text-muted);
}
</style>
